import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import type { MessageDemande } from '../../types';
import { MessagesSms } from './MessagesSms';
import { annoncerMiseAJourMessages } from './useCompteMessages';
import { jourLocalIso } from '../demande/horaire';

/** « aujourd'hui », « demain », sinon « jeudi 22 octobre » : le titre disait toujours « aujourd'hui ». */
function quandLaSeance(dateISO: string): string {
  const auj = jourLocalIso();
  if (dateISO === auj) return "aujourd'hui";
  const demain = new Date(); demain.setDate(demain.getDate() + 1);
  if (dateISO === jourLocalIso(demain)) return 'demain';
  return new Intl.DateTimeFormat('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(`${dateISO}T12:00:00`));
}

interface Lot { demandeId: number; atelierNom: string; dateAtelier: string; messages: MessageDemande[]; }

/**
 * Console Urgence : les SMS d'annulation du jour qui attendent d'être envoyés (docs/DECISIONS.md §24). Un lot reste affiché,
 * avec « Envoyés par … », tant que la page est ouverte — on voit que c'est fait, au lieu qu'il disparaisse sans un mot.
 * `version` change quand une annulation vient d'être confirmée : la liste se recharge.
 */
export function SmsAEnvoyer({ version }: { version: number }) {
  const [lots, setLots] = useState<Lot[]>([]);
  const [vus, setVus] = useState<Record<number, { nom: string; date: string }>>({});

  const charger = useCallback(async () => {
    try {
      const groupes = (await api.getMessagesAEnvoyer()).groupes.filter((g) => g.canal === 'sms');
      const connus = { ...vus };
      for (const g of groupes) connus[g.demande_id] = { nom: g.atelier_nom ?? 'l\'atelier', date: g.date_atelier };
      setVus(connus);
      const lotsCharges = await Promise.all(Object.entries(connus).map(async ([id, { nom, date }]) => ({
        demandeId: Number(id), atelierNom: nom, dateAtelier: date,
        messages: (await api.getMessagesDemande(Number(id))).filter((m) => m.canal === 'sms'),
      })));
      setLots(lotsCharges.filter((l) => l.messages.length > 0));
    } catch { /* confort : la console reste utilisable sans ce bloc */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vus]);

  useEffect(() => { void charger(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [version]);
  if (lots.length === 0) return null;

  return (
    <section className="card mb-16" aria-labelledby="sms-a-envoyer-titre">
      <h3 className="card-title" id="sms-a-envoyer-titre">SMS d'annulation à envoyer</h3>
      <p className="text-sm text-muted mb-12">
        {"L'atelier est annulé : envoyez le SMS aux participant·e·s maintenant. L'app ne l'envoie pas toute seule ; indiquez ensuite que c'est fait."}
      </p>
      <div className="msg-pile">
        {lots.map((l) => (
          <MessagesSms key={l.demandeId} demandeId={l.demandeId} messages={l.messages} titre={`Annulation de ${l.atelierNom}, ${quandLaSeance(l.dateAtelier)}`}
            onChange={() => { annoncerMiseAJourMessages(); void charger(); }} />
        ))}
      </div>
    </section>
  );
}
